import { useMemo, useState } from 'react';
import { Plus, Search, Pencil, Trash2, Loader2, Minus, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useAdjustUserTokens,
  useAdminRoles,
  useAdminUsers,
  useDeleteUser,
} from '@/features/admin/useAdmin';
import { UserFormDialog } from '@/components/dialogs/admin/UserFormDialog';
import { ConfirmDialog } from '@/components/dialogs/admin/ConfirmDialog';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { RoleBadges } from '@/features/billing/RolesAndBadges';
import { TokensEditorDialog } from '@/components/dialogs/admin/TokensEditorDialog';
import { useAuthStore } from '@/stores/authStore';
import type { AdminUser } from '@/features/admin/types';

export function UsersTab() {
  const me = useAuthStore((s) => s.user);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [editingTokens, setEditingTokens] = useState<AdminUser | null>(null);

  const usersQ = useAdminUsers(q);
  const rolesQ = useAdminRoles();
  const del = useDeleteUser();
  const adjustTokens = useAdjustUserTokens();

  const roles = useMemo(() => rolesQ.data ?? [], [rolesQ.data]);
  const users = usersQ.data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Usuarios
        </h2>
        <span className="font-mono tabular text-xs" style={{ color: 'var(--text-muted)' }}>
          {users.length}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search
              size={12}
              strokeWidth={1.8}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--text-muted)' }}
            />
            <input
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
              placeholder="Buscar..."
              className="h-9 w-56 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] pl-7 pr-2 text-sm focus:border-[var(--accent)] focus:outline-none"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
            />
          </div>
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus size={14} strokeWidth={2} /> Nuevo usuario
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-md)] border" style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
        <table className="w-full text-left text-sm">
          <thead style={{ background: 'var(--surface-2)' }}>
            <tr className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              <Th>Email</Th>
              <Th>Nombre</Th>
              <Th>Rol</Th>
              <Th>Fierros</Th>
              <Th>Estado</Th>
              <Th>Creado</Th>
              <Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {usersQ.isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="mr-2 inline animate-spin" /> Cargando usuarios...
                </td>
              </tr>
            )}
            {users.map((u) => {
              const isMe = me?.id === u.id;
              return (
                <tr
                  key={u.id}
                  className="cursor-pointer border-t transition-colors duration-150 hover:bg-[var(--surface-2)]"
                  style={{ borderColor: 'var(--border-subtle)' }}
                  onClick={() => setEditing(u)}
                >
                  <Td>
                    <span className="font-mono tabular">{u.email}</span>
                    {isMe && (
                      <span
                        className="ml-2 rounded-[var(--radius-sm)] border px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
                        style={{ borderColor: 'rgba(245,158,11,0.30)', color: 'var(--accent)' }}
                      >
                        tu
                      </span>
                    )}
                  </Td>
                  <Td>{u.name || <span style={{ color: 'var(--text-muted)' }}>-</span>}</Td>
                  <Td>
                    {/* Show the role pill PLUS any derived status badges
                        (currently: "Suscriptor" when subscription_active). */}
                    <RoleBadges user={u} />
                  </Td>
                  <Td>
                    <FierrosCell
                      user={u}
                      busy={adjustTokens.isPending}
                      onAdjust={(delta) =>
                        adjustTokens.mutate({ user_id: u.id, amount: delta })
                      }
                      onEdit={() => setEditingTokens(u)}
                    />
                  </Td>
                  <Td>
                    <StatusPill active={u.is_active} />
                  </Td>
                  <Td>
                    <span className="font-mono tabular text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" aria-label="Editar" onClick={() => setEditing(u)}>
                        <Pencil size={12} strokeWidth={1.8} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Eliminar"
                        disabled={isMe}
                        title={isMe ? 'No puedes eliminar tu propia cuenta' : 'Eliminar'}
                        onClick={() => setDeleting(u)}
                      >
                        <Trash2 size={12} strokeWidth={1.8} style={{ color: isMe ? 'var(--text-disabled)' : 'var(--danger)' }} />
                      </Button>
                    </div>
                  </Td>
                </tr>
              );
            })}
            {!usersQ.isLoading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  Sin coincidencias.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <UserFormDialog open={creating} onOpenChange={setCreating} roles={roles} />
      <UserFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} user={editing} roles={roles} />
      <TokensEditorDialog
        user={editingTokens}
        onOpenChange={(o) => !o && setEditingTokens(null)}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Eliminar ${deleting?.email ?? ''}?`}
        description="Esta accion es irreversible. Se borraran las referencias del usuario, pero el log de auditoria se conserva."
        confirmText={del.isPending ? 'Eliminando...' : 'Eliminar'}
        loading={del.isPending}
        onConfirm={async () => {
          if (deleting) await del.mutateAsync(deleting.id);
        }}
      />
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 ${className}`}>{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 ${className}`}>{children}</td>;
}

/**
 * Inline fierros editor. Shows the current balance with a "−" / "+" pair for
 * 1-step adjustments and a settings icon to open the larger editor dialog
 * for arbitrary amounts. Subscribed users show "∞" since the balance is
 * irrelevant while they have unlimited exports.
 */
function FierrosCell({
  user,
  busy,
  onAdjust,
  onEdit,
}: {
  user: AdminUser;
  busy: boolean;
  onAdjust: (delta: number) => void;
  onEdit: () => void;
}) {
  const balance = user.tokens_balance ?? 0;
  const subByDate = !!(
    user.subscription_status === 'active' &&
    user.subscription_expires_at &&
    new Date(user.subscription_expires_at).getTime() > Date.now()
  );
  // The seeded `subscriber` role (or any role with `billing.unlimited`)
  // is also treated as unlimited — admins can grant the role manually as
  // an alternative to the date-based subscription.
  const subByRole =
    user.role.slug === 'subscriber' ||
    (user.role.permissions ?? []).includes('billing.unlimited');
  const subActive = subByDate || subByRole;

  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <span className="inline-flex items-center gap-1 font-mono tabular text-sm">
        <FierroIcon size={12} />
        {subActive ? <span style={{ color: 'var(--accent)' }}>∞</span> : balance}
      </span>
      {!subActive && (
        <>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Quitar 1 fierro"
            onClick={() => onAdjust(-1)}
            disabled={busy || balance <= 0}
            title="Quitar 1 fierro"
          >
            <Minus size={11} strokeWidth={1.8} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Añadir 1 fierro"
            onClick={() => onAdjust(1)}
            disabled={busy}
            title="Añadir 1 fierro"
          >
            <Plus size={11} strokeWidth={1.8} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Ajustar fierros"
            onClick={onEdit}
            title="Ajustar cantidad personalizada"
          >
            <Settings2 size={11} strokeWidth={1.8} />
          </Button>
        </>
      )}
    </div>
  );
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px]"
      style={{
        background: active ? 'rgba(34,197,94,0.10)' : 'rgba(148,163,184,0.10)',
        color: active ? 'var(--success)' : 'var(--text-muted)',
      }}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: active ? 'var(--success)' : 'var(--text-muted)' }} />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  );
}
